import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  TextField,
  ThemeProvider,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import AuthLayout, { AuthLink } from './components/AuthLayout';
import { theme } from './theme';

export default function Register(_props: { theme?: any }) {
  const { t } = useTranslation('auth');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sendMarketing, setSendMarketing] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [referredBy, setReferredBy] = useState<string | null>(null);
  // Wave 20.41 — Honeypot. Rendered as an off-screen input so no
  // real user ever sees it. Bots that autofill every form field
  // will trip this + the server rejects. `website` is a common
  // real field name so bots take the bait.
  const [website, setWebsite] = useState('');

  useEffect(() => {
    const params = Object.fromEntries(
      new URLSearchParams(window.location.search).entries()
    );
    if (params.referredby) setReferredBy(params.referredby);
  }, []);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMsg(null);

    if (!firstName || !lastName || !email || !password || !confirmPassword) {
      setErrorMsg(t('register.errorAllFields'));
      return;
    }
    if (password.length < 8) {
      setErrorMsg(t('register.errorPasswordLength'));
      return;
    }
    if (password !== confirmPassword) {
      setErrorMsg(t('register.errorPasswordMatch'));
      return;
    }

    setLoading(true);
    fetch(`${process.env.REACT_APP_API}/users/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName,
        lastName,
        email: email.trim().toLowerCase(),
        password,
        settings: { sendMarketingEmails: sendMarketing },
        referredBy,
        website,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.statusCode && data.statusCode > 201)
          throw new Error(data.message || 'Signup failed');
        return data;
      })
      .then(() => {
        setSuccessMsg(t('register.success', { email }));
        setLoading(false);
      })
      .catch((e) => {
        setErrorMsg(e.message || t('register.errorGeneric'));
        setLoading(false);
      });
  };

  return (
    <ThemeProvider theme={theme}>
      <AuthLayout
        title={t('register.title')}
        subtitle={t('register.subtitle')}
        footerLeft={
          <span style={{ color: '#8B95A8' }}>
            {t('register.alreadyMember')} <AuthLink href="/login">{t('register.signIn')}</AuthLink>
          </span>
        }
      >
        {errorMsg && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errorMsg}
          </Alert>
        )}
        {successMsg && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {successMsg}
          </Alert>
        )}

        {/* Hide the form after a successful signup so a user re-reading
            the "check your email" message doesn't re-submit — the
            second submit 409s on duplicate email and confuses them. */}
        {!successMsg && (
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* Wave 20.41 — Honeypot. Off-screen but present in the DOM.
              tabIndex=-1 + aria-hidden so screen readers skip it. Real
              humans never see or interact with it. Bots autofilling
              every input trip the server-side @MaxLength(0) check. */}
          <input
            type="text"
            name="website"
            autoComplete="off"
            tabIndex={-1}
            aria-hidden="true"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: 'auto',
              width: 1,
              height: 1,
              overflow: 'hidden',
            }}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('register.firstName')}
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('register.lastName')}
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('register.email')}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('register.password')}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                helperText={t('register.passwordHelp')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('register.confirmPassword')}
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            sx={{ mt: 2, color: '#8B95A8' }}
            control={
              <Checkbox
                checked={sendMarketing}
                onChange={(e) => setSendMarketing(e.target.checked)}
                sx={{ color: '#8B95A8', '&.Mui-checked': { color: '#6AFF45' } }}
              />
            }
            label={
              <span style={{ fontSize: '0.875rem' }}>
                {t('register.marketingOptIn')}
              </span>
            }
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            size="large"
            disabled={loading}
            sx={{ py: 1.5, mt: 3, fontSize: '1rem' }}
          >
            {loading ? (
              <CircularProgress size={22} sx={{ color: '#0A0E1A' }} />
            ) : (
              t('register.createAccount')
            )}
          </Button>
        </Box>
        )}
      </AuthLayout>
    </ThemeProvider>
  );
}
